import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icons } from '@/utils/Icons';
import CustomInput from '@/components/common/CustomInput';
import { useNavigate } from 'react-router-dom';
import { useRegister } from '@/pages/Auth/hooks/useRegister';
import CustomInputTwo from './common/CustomInputTwo';
import { Control, Controller, FieldError } from 'react-hook-form';
import PhoneInput from './common/PhoneInput';
import { Loader2 } from 'lucide-react';
import OtpTimer from './common/OtpTimer';
import { buildTelegramLink, buildWhatsappLink, socialLinks } from '@/utils/constants';
import { useGetContactSupportQuery } from '@/apis/userApi';
import { useLockBodyScroll } from '@/hooks';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupModal = ({ isOpen, onClose }: SignupModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useLockBodyScroll(isOpen);

  const {
    form,
    isValidField,
    phone,
    setPhone,
    showOtpField,
    timeLeft,
    active,
    referralCode,
    otpLoading,
    registerLoading,
    handleSendOtp,
    handleResetOtpField,
    handleSubmitForm,
  } = useRegister();

  const initialPhone = {
    country: '',
    countryCode: '',
    fullNumber: '',
    phoneNumber: '',
  };

  const { data: contactSupport } = useGetContactSupportQuery();

  const whatsappLink = buildWhatsappLink(contactSupport?.whatsapp);
  const telegramLink = buildTelegramLink(contactSupport?.telegram);

  const modalSocialLinks = [
    ...(whatsappLink ? [{ platform: 'whatsapp', url: whatsappLink }] : []),
    ...(telegramLink ? [{ platform: 'telegram', url: telegramLink }] : []),
    ...socialLinks,
  ];

  const { control, handleSubmit, reset } = form;

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const handleClose = () => {
    reset();
    handleResetOtpField();
    setPhone(initialPhone);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-2 md:px-4"
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-[400px] rounded-[10px] border-[3px] border-[#ff9500]  p-[14px] text-xs shadow-2xl bg-modelBg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 text-registerText transition hover:text-red-500"
        >
          <Icons.Cross2 size={12} />
        </button>

        {/* Header */}
        <h2 className="mb-3 text-center text-base font-semibold text-registerText">
          {t('auth.register.title')}
        </h2>
        <div className="mb-6 border-b-2 border-registerBottomBorder" />

        <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-1 xl:space-y-2">
          {/* First + Last Name */}
          <div className="flex gap-2">
            <CustomInputTwo
              name="firstName"
              control={control}
              label={t('auth.register.firstName')}
              placeholder="John"
              className="flex-1"
              inputRules={{
                onlyAlphabet: true,
                maxLength: 50,
                noSpace: true,
              }}
            />
            <CustomInputTwo
              name="lastName"
              control={control}
              label={t('auth.register.lastName')}
              placeholder="Doe"
              className="flex-1"
              inputRules={{
                onlyAlphabet: true,
                maxLength: 50,
                noSpace: true,
              }}
            />
          </div>

          {/* Username */}
          <CustomInputTwo
            name="username"
            control={control}
            label={t('auth.register.username')}
            placeholder="username"
            autoComplete="username"
            inputRules={{
              pattern: /^[a-zA-Z0-9]+$/,
              noSpace: true,
              maxLength: 50,
            }}
          />

          {/* Phone + OTP */}
          <div className="space-y-2">
            <label htmlFor="phone" className="text-registerText text-sm">
              {t('auth.phoneNumber')}
            </label>

            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="flex gap-2">
                    <PhoneInput
                      value={field.value}
                      onChange={(phoneData) => {
                        setPhone(phoneData);
                        return field.onChange(phoneData.fullNumber);
                      }}
                      disabled={active}
                    />

                    <button
                      type="button"
                      onClick={active ? handleResetOtpField : handleSendOtp}
                      disabled={otpLoading}
                      className="shrink-0 rounded-[4px] border border-orange-400 bg-[#08061A] px-3 text-xs font-semibold text-white transition enabled:hover:bg-white enabled:hover:text-black disabled:opacity-60"
                    >
                      {otpLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : active ? (
                        t('auth.changeNumber')
                      ) : (
                        t('auth.sendOtp')
                      )}
                    </button>
                  </div>
                  {error && <p className="text-xs text-red-600">{error.message}</p>}
                </>
              )}
            />

            {showOtpField && (
              <>
                <div className="w-fit ml-auto">
                  <OtpTimer timeLeft={timeLeft} active={active} onResend={handleSendOtp} />
                </div>

                <CustomInput
                  name="otp"
                  control={control}
                  label={t('auth.register.otp')}
                  type="text"
                  inputType="numeric"
                  placeholder={t('auth.otpPlaceholder')}
                  className="!mt-0"
                  maxLength={6}
                />
              </>
            )}
          </div>

          {/* Referral */}
          <CustomInputTwo
            name="referralCode"
            control={control}
            label={t('auth.register.referralCode')}
            type="text"
            placeholder="916B65CF"
            autoComplete="referral"
            disabled={referralCode ? true : false}
            inputClassName="disabled:opacity-100"
          />

          {/* Password */}
          <CustomInputTwo
            name="password"
            control={control}
            label={t('auth.register.password')}
            type="password"
            placeholder="Abcd@1234"
            autoComplete="new-password"
            inputRules={{
              noSpace: true,
              maxLength: 20,
            }}
          />

          {/* Submit Button */}
          <div className="!mt-4 w-full flex justify-center">
            <button
              type="submit"
              disabled={registerLoading || !isValidField}
              className="flex h-11 w-full items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition enabled:hover:bg-white enabled:hover:text-black disabled:opacity-60"
            >
              {registerLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t('auth.register.submit')
              )}
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="my-4 border-b border-dashed border-newLoginHeader" />

        {/* Social Section */}
        <div className="text-center">
          <p className="mb-4 text-[12px] text-registerText">{t('auth.connectWithUs')}</p>
          <div className="flex flex-wrap justify-center gap-1">
            {modalSocialLinks.map(({ platform, url }) => {
              if (!url) return null;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-black bg-[#FCFCFC] hover:bg-[#EBBE71]"
                >
                  <img src={`/images/social/${platform}.png`} alt={platform} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SignupModal;
